import { Typography } from '@material-ui/core';
import { Party } from 'api/file/interfaces/get-file-order-insights';
import clsx from 'clsx';
import { ProfileContext, ProfileContextInterface } from 'hooks/ProfileContext';
import { useMarketPlace } from 'hooks/useMarketPlace';
import { useContext, useMemo, useState } from 'react';
import { AgentNet_Admin, FRAUD_VALIDATION, FRAUD_VALIDATION_READ_ONLY } from 'utilities/constants/activity-rights';
import DisabledProducts from './DisabledProducts';
import useStyles from './ListStyles';
import OrderInsightsRiskIcon from './OrderInsightsRiskIcon';
import VerifyPhoneNumber from './VerifyPhoneNumber';

interface IValidation {
  qaPrefix: string;
  title: string;
  description: string;
  type?: 'ContactValidation' | 'EnhancedIdentityVerification';
  party: Party;
  isEnabled: boolean;
  productCode: string;
  hasRisks: boolean;
  index: number;
}

const Validation = ({
  party,
  qaPrefix,
  title,
  description,
  type = 'ContactValidation',
  isEnabled,
  productCode,
  hasRisks,
  index,
}: IValidation) => {
  const classes = useStyles();

  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};
  const { userFirm, profile } = profileCtx;

  // @todo: need to change into useReducer if needed based on future story
  const [modalStepsStatus, setModalStepsStatus] = useState({
    isRiskChecked: false,
    isRiskFound: false,
    isResponsePendingFromParty: false,
    isVerifcationNotDone: false,
  });

  const { isRiskChecked, isRiskFound, isResponsePendingFromParty, isVerifcationNotDone } = modalStepsStatus;
  const {
    doValidateContactInfo,
    validateContactInfoStatus,
    validateContactInfoStatusResults,
    enhancedIdentityVerification,
    enhancedIdentityVerificationStatusResults,
    enhancedIdentityVerificationStatus,
  } = useMarketPlace();

  const hasFraudValidation = useMemo(() => {
    return profile?.activityRights?.some((rights) => rights.ActivityRightId === FRAUD_VALIDATION);
  }, [profile?.activityRights]);

  const hasReadOnlyFraudValidation = useMemo(() => {
    return profile?.activityRights?.some((rights) => rights.ActivityRightId === FRAUD_VALIDATION_READ_ONLY);
  }, [profile?.activityRights]);

  const isAgentNetAdmin = useMemo(() => {
    return profile?.activityRights?.some((rights) => rights.ActivityRightId === AgentNet_Admin);
  }, [profile?.activityRights]);

  const riskIcon = useMemo(() => {
    const risksCount = validateContactInfoStatusResults?.risks?.length;
    const risks = hasRisks || risksCount;

    if (!isEnabled || !(hasFraudValidation || hasReadOnlyFraudValidation)) return 'Instruction';
    if (type === 'EnhancedIdentityVerification' && !party.phoneNumber) return 'Error';

    if (party.phoneNumber || party.emailAddress) {
      if (
        type === 'EnhancedIdentityVerification' &&
        (party.enhancedIdentifyVerification?.transactionStatus === 'Pending' ||
          enhancedIdentityVerificationStatusResults?.transactionStatus === 'Pending')
      )
        return 'Pending';

      if (
        type === 'EnhancedIdentityVerification' &&
        (party.enhancedIdentifyVerification?.transactionStatus === 'Failed' ||
          enhancedIdentityVerificationStatusResults?.transactionStatus === 'Failed')
      )
        return 'Error';

      if (
        (type === 'ContactValidation' && (party?.contactValidation || validateContactInfoStatusResults)) ||
        (type === 'EnhancedIdentityVerification' && party?.enhancedIdentifyVerification)
      )
        return risks ? 'Alert' : 'Success';

      if (!risks) return 'Loading';
    } else {
      return 'Error';
    }
  }, [
    type,
    isEnabled,
    party,
    validateContactInfoStatus,
    hasFraudValidation,
    hasReadOnlyFraudValidation,
    validateContactInfoStatusResults,
    enhancedIdentityVerificationStatusResults,
  ]);

  return (
    <div className={classes.brdBtm}>
      <div className={classes.flex}>
        <div className={classes.labelCol}>
          <span className={classes.flexCenter}>
            <OrderInsightsRiskIcon riskSeverity={riskIcon} />
            <Typography
              variant="h4"
              color="textSecondary"
              className={clsx(classes.marginLeft, classes.typographyFontSize)}
            >
              {title}:
            </Typography>
          </span>
        </div>
        <div className={classes.flexGrow}>
          {isEnabled ? (
            <VerifyPhoneNumber
              firmId={userFirm?.firmId ?? ''}
              hasReadOnlyFraudValidation={hasReadOnlyFraudValidation}
              hasFraudValidation={hasFraudValidation}
              party={party}
              type={type}
              index={index}
              description={description}
              isResponsePendingFromParty={false}
              isVerifcationNotDone={false}
              isRiskChecked={false}
              isRiskFound={false}
              qaPrefix={qaPrefix}
              validationChecks={{
                doValidateContactInfo,
                validateContactInfoStatus,
                validateContactInfoStatusResults,
                enhancedIdentityVerification,
                enhancedIdentityVerificationStatusResults,
                enhancedIdentityVerificationStatus,
              }}
            />
          ) : (
            <DisabledProducts
              qaPrefix={qaPrefix}
              index={index}
              description={description}
              productCode={productCode}
              isAgentNetAdmin={isAgentNetAdmin}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Validation;
